import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { ArrowLeft, Instagram, Mail, Droplets, Sun, CloudRain, Shield, Sparkles, FlaskConical, Check, Wind, Scissors, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ARTICLE_IMAGES = {
  'why-indian-men-need-different-skincare': 'https://images.unsplash.com/photo-1710395063369-cbdad5e70610?auto=format&fit=crop&w=900&q=80',
  'the-white-cast-problem-solved': 'https://images.unsplash.com/photo-1725119510161-10b53b29670714?auto=format&fit=crop&w=900&q=80',
  'niacinamide-the-one-ingredient': 'https://static.prod-images.emergentagent.com/jobs/5d269af6-e239-4577-baf0-94dde8e2fa47/images/0cf26c34d4d110ad6552f8b0066cd5fc41772b326bcbc3b27add9adce34d5052.png',
  'why-oily-skin-needs-moisturiser': 'https://images.unsplash.com/photo-1595776042969-89d50e7e47f6?auto=format&fit=crop&w=900&q=80',
  'what-spf-numbers-mean': 'https://static.prod-images.emergentagent.com/jobs/5d269af6-e239-4577-baf0-94dde8e2fa47/images/450acc76584999e808e9c7e4128afaac2cd6f736a02054f8daa8e03649e8d91f.png',
  'the-scalp-is-skin-same-rules-apply': 'https://static.prod-images.emergentagent.com/jobs/5d269af6-e239-4577-baf0-94dde8e2fa47/images/e891ab9ff3247b938261353d229048e69844615a4365660cffad83d87921d560.png',
  'why-body-skin-needs-the-same-science': 'https://images.unsplash.com/photo-1596181576841-9bf4d437dd9e?auto=format&fit=crop&w=900&q=80',
};

const mdBold = (text) => DOMPurify.sanitize(
  text.replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#0C4A8C] font-semibold">$1</strong>'),
  { ALLOWED_TAGS: ['strong'], ALLOWED_ATTR: ['class'] }
);

const sectionIcon = (heading) => {
  const h = heading.toLowerCase();
  if (h.includes('water')) return Droplets;
  if (h.includes('uv') || h.includes('spf') || h.includes('sun') || h.includes('cast')) return Sun;
  if (h.includes('humid') || h.includes('monsoon') || h.includes('moistur')) return CloudRain;
  if (h.includes('pollut') || h.includes('air')) return Wind;
  if (h.includes('scalp') || h.includes('hair')) return Scissors;
  if (h.includes('skin type') || h.includes('melanin') || h.includes('tone')) return User;
  if (h.includes('ph') || h.includes('formul') || h.includes('ingredient') || h.includes('niacinamide') || h.includes('acid')) return FlaskConical;
  if (h.includes('barrier') || h.includes('protect') || h.includes('defen')) return Shield;
  return Sparkles;
};

const STAT_REGEX = /(\d+[-–]\d+\s?(?:ppm|%)|\d+\s?ppm|SPF\s?\d+\+?|pH\s?[\d.]+(?:[-–][\d.]+)?|\d+[-–]\d+%|\d+%|UV(?:\sindex)?\s\d+[-–]\d+|Type\s[IVX]+[-–][IVX]+)/i;

function parseArticle(text) {
  if (!text) return { intro: [], sections: [], outro: [] };
  const blocks = text.split('\n\n').map(b => b.trim()).filter(Boolean);
  const intro = [];
  const sections = [];
  const outro = [];
  let current = null;

  for (const block of blocks) {
    const headingWithBody = block.match(/^\*\*([^*\n]+)\*\*\n([\s\S]+)$/);
    if (block.startsWith('**') && block.endsWith('**') && !block.includes('\n') && !block.slice(2, -2).includes('**')) {
      current = { heading: block.replace(/\*\*/g, ''), paras: [], bullets: [] };
      sections.push(current);
    } else if (headingWithBody) {
      current = { heading: headingWithBody[1].trim(), paras: [], bullets: [] };
      sections.push(current);
      const body = headingWithBody[2].trim();
      const items = body.split('\n').filter(l => l.startsWith('- ')).map(l => l.slice(2));
      const paras = body.split('\n').filter(l => l.trim() && !l.startsWith('- '));
      current.paras.push(...paras);
      current.bullets.push(...items);
    } else if (block.includes('\n- ') || block.startsWith('- ')) {
      const lines = block.split('\n');
      const lead = lines[0].startsWith('- ') ? null : lines[0];
      const items = lines.filter(l => l.startsWith('- ')).map(l => l.slice(2));
      const target = current || (sections.length ? sections[sections.length - 1] : null);
      if (target) {
        if (lead) target.paras.push(lead);
        target.bullets.push(...items);
      } else {
        if (lead) intro.push(lead);
        sections.push({ heading: null, paras: [], bullets: items });
      }
    } else if (current) {
      if (block.startsWith('TRUFORM')) outro.push(block);
      else current.paras.push(block);
    } else {
      intro.push(block);
    }
  }
  return { intro, sections, outro };
}

function extractStat(section) {
  const text = [section.heading || '', ...section.paras, ...section.bullets].join(' ');
  const m = text.match(STAT_REGEX);
  return m ? m[0] : null;
}

function ArticleVisual({ article, slug }) {
  const { intro, sections, outro } = parseArticle(article.content);
  const img = ARTICLE_IMAGES[slug];

  return (
    <div className="pb-4 pt-3">
      {/* Visual header */}
      {img && (
        <div className="relative h-36 md:h-44 rounded-[3px] overflow-hidden mb-5">
          <img src={img} alt={article.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(6,43,82,0.85) 0%, rgba(6,43,82,0.1) 60%)' }} />
          <span className="absolute bottom-3 left-4 text-[9px] font-sans font-semibold uppercase tracking-[2px] px-2.5 py-1 rounded-[2px] bg-[#BAE6FD] text-[#062B52]">{article.category}</span>
        </div>
      )}

      {/* Lead */}
      {intro.map((p) => (
        <p key={p.slice(0, 30)} className="text-[14px] text-[#3D4F5C] font-light leading-relaxed mb-4"
          dangerouslySetInnerHTML={{ __html: mdBold(p) }} />
      ))}

      {/* Visual section cards */}
      {sections.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2" data-testid={`article-cards-${slug}`}>
          {sections.map((s, i) => {
            const Icon = sectionIcon(s.heading || '');
            const stat = extractStat(s);
            return (
              <div key={s.heading || `s-${i}`}
                className={`rounded-[3px] bg-[#F0F9FF] p-4 flex flex-col ${!s.heading && s.bullets.length > 2 ? 'sm:col-span-2' : ''}`}
                style={{ border: '1px solid rgba(12,74,140,0.08)', borderTop: '2px solid #0C7BC0' }}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-[#0C4A8C] flex items-center justify-center flex-shrink-0">
                      <Icon size={14} className="text-[#BAE6FD]" />
                    </div>
                    {s.heading && <p className="font-serif text-[17px] font-semibold text-[#0C4A8C] leading-tight">{s.heading}</p>}
                  </div>
                  {stat && (
                    <span className="flex-shrink-0 font-serif text-[18px] font-bold text-[#0C7BC0] leading-none whitespace-nowrap pt-1.5">{stat}</span>
                  )}
                </div>
                {s.paras.map((p) => (
                  <p key={p.slice(0, 30)} className="text-[12px] text-[#3D4F5C] font-light leading-relaxed mb-2"
                    dangerouslySetInnerHTML={{ __html: mdBold(p) }} />
                ))}
                {s.bullets.length > 0 && (
                  <ul className="space-y-1.5 mt-1">
                    {s.bullets.map((b) => (
                      <li key={b.slice(0, 30)} className="flex items-start gap-2">
                        <Check size={12} className="text-[#0C7BC0] flex-shrink-0 mt-0.5" />
                        <span className="text-[12px] text-[#3D4F5C] font-light leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: mdBold(b) }} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Conclusion */}
      {outro.map((p) => (
        <div key={p.slice(0, 30)} className="mt-4 p-4 rounded-[3px] bg-[#0C4A8C]" data-testid={`article-outro-${slug}`}>
          <p className="text-[12px] text-white/75 font-light leading-relaxed"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>'), { ALLOWED_TAGS: ['strong'], ALLOWED_ATTR: ['class'] }) }} />
        </div>
      ))}
    </div>
  );
}

export default function SciencePage() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    axios.get(`${API}/articles`)
      .then(r => setArticles(r.data))
      .catch(err => console.error('Failed to load articles:', err));
    window.scrollTo(0, 0);
  }, []);
  const [fullArticles, setFullArticles] = useState({});

  const loadArticle = async (slug) => {
    if (fullArticles[slug]) return;
    try {
      const r = await axios.get(`${API}/articles/${slug}`);
      setFullArticles(prev => ({ ...prev, [slug]: r.data }));
    } catch (err) {
      console.error('Failed to load article:', err);
    }
  };

  return (
    <div className="bg-[#F0F9FF]">
      {/* Header */}
      <section className="bg-[#0C4A8C] py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <button onClick={() => navigate(-1)} data-testid="back-button" className="inline-flex items-center gap-2 text-white/30 hover:text-white text-[12px] mb-6 transition-colors">
            <ArrowLeft size={13} /> Back
          </button>
          <p className="eyebrow mb-3 text-[#BAE6FD]">The Science</p>
          <h1 data-testid="science-heading" className="text-4xl sm:text-5xl font-serif font-medium text-white tracking-tight">We explain everything.</h1>
          <p className="text-[14px] mt-3 font-light text-white/40">Every ingredient. Every concentration. Every formulation decision.</p>
        </div>
      </section>

      {/* Articles */}
      <section className="bg-white max-w-3xl mx-auto mt-5 mb-5 rounded-[4px] py-8 md:py-12 px-6 md:px-10" style={{ boxShadow: '0 1px 3px rgba(12,74,140,0.06)' }}>
        <Accordion type="single" collapsible className="w-full">
          {articles.map((a) => (
            <AccordionItem key={a.slug} value={a.slug} style={{ borderColor: 'rgba(12,74,140,0.06)' }}>
              <AccordionTrigger
                data-testid={`article-trigger-${a.slug}`}
                className="text-left hover:no-underline py-5"
                onClick={() => loadArticle(a.slug)}
              >
                <div className="flex-1 pr-4">
                  <span className="eyebrow text-[#0C7BC0] block mb-1.5">{a.category}</span>
                  <span className="font-serif text-lg text-[#0C4A8C] font-medium leading-tight block">{a.title}</span>
                  <span className="text-[12px] text-[#6B7D8A] font-sans font-light block mt-1">{a.read_time}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                {fullArticles[a.slug]
                  ? <ArticleVisual article={fullArticles[a.slug]} slug={a.slug} />
                  : <p className="text-sm text-[#6B7D8A] pb-4">Loading...</p>}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Footer CTA */}
      <section className="bg-[#0C4A8C] py-12 md:py-16 max-w-3xl mx-auto mb-5 rounded-[4px]">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <p className="font-serif italic text-[16px] mb-5 text-white/45">Questions? Ask us anything.</p>
          <div className="flex items-center justify-center gap-6">
            <a href="https://instagram.com/truformmen" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white/35 hover:text-white text-[12px] transition-colors">
              <Instagram size={15} /> @truformmen
            </a>
            <a href="mailto:hello@truformmen.com" className="flex items-center gap-2 text-white/35 hover:text-white text-[12px] transition-colors">
              <Mail size={15} /> hello@truformmen.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
